import React, { useState, useEffect, useCallback } from 'react';
import { Plug, Key, Webhook, Layers, Loader2, Receipt } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';
import { ApiKeyTab } from './provisioning/ApiKeyTab';
import { WebhookTab } from './provisioning/WebhookTab';
import { PlansTab } from './provisioning/PlansTab';
import { BillingTab } from './provisioning/BillingTab';
import { InvoiceDialog } from './provisioning/InvoiceDialog';
import { ConfirmAddonDialog } from './provisioning/ConfirmAddonDialog';
import { ConfirmRotateDialog } from './provisioning/ConfirmRotateDialog';
import { NewKeyDialog } from './provisioning/NewKeyDialog';

const ProvisioningPage = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [plans, setPlans] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [fixedKeyInput, setFixedKeyInput] = useState('');
  const [settingKey, setSettingKey] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingPlan, setSavingPlan] = useState('');
  const [billing, setBilling] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [addonForm, setAddonForm] = useState({});
  const [busyTenant, setBusyTenant] = useState('');
  const [confirmAddon, setConfirmAddon] = useState(null);
  const [invoice, setInvoice] = useState(null);

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    try {
      const [cfgRes, plansRes, billingRes, logsRes] = await Promise.all([
        api.get('/super-admin/provisioning-config'),
        api.get('/super-admin/plans'),
        api.get('/super-admin/billing'),
        api.get('/super-admin/webhook-logs'),
      ]);
      setConfig(cfgRes.data);
      setWebhookUrl(cfgRes.data.webhook_url || '');
      setWebhookEnabled(cfgRes.data.webhook_enabled || false);
      setPlans(plansRes.data.plans || []);
      setBilling(billingRes.data.tenants || []);
      setWebhookLogs(logsRes.data.logs || []);
    } catch (err) {
      setError('Erro ao carregar configuração de provisionamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const handleRotate = async () => {
    setConfirmRotate(false);
    setRotating(true);
    setError('');
    try {
      const res = await api.post('/super-admin/provisioning-key/rotate');
      setNewKey(res.data.api_key);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao gerar nova chave');
    } finally {
      setRotating(false);
    }
  };

  const handleSetKey = async () => {
    setSettingKey(true);
    setError('');
    try {
      await api.post('/super-admin/provisioning-key/set', { api_key: fixedKeyInput.trim() });
      setFixedKeyInput('');
      flash('Chave de provisionamento definida com sucesso');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao definir a chave');
    } finally {
      setSettingKey(false);
    }
  };

  const handleSaveWebhook = async () => {
    setSavingWebhook(true);
    setError('');
    try {
      const res = await api.put('/super-admin/provisioning-webhook', {
        url: webhookUrl, enabled: webhookEnabled,
      });
      if (res.data.signing_secret) setWebhookSecret(res.data.signing_secret);
      flash('Webhook guardado com sucesso');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar webhook');
    } finally {
      setSavingWebhook(false);
    }
  };

  const updatePlanField = (key, field, value) => {
    setPlans(prev => prev.map(p => p.key === key ? { ...p, [field]: value } : p));
  };

  const handleSavePlan = async (plan) => {
    setSavingPlan(plan.key);
    setError('');
    try {
      await api.put(`/super-admin/plans/${plan.key}`, {
        name: plan.name,
        max_users: parseInt(plan.max_users),
        max_reports_month: parseInt(plan.max_reports_month),
        price_monthly: parseFloat(plan.price_monthly),
        default_overage_mode: plan.default_overage_mode || 'billing_only',
        extra_report_unit_price: parseFloat(plan.extra_report_unit_price || 0),
        is_active: plan.is_active,
      });
      flash(`Plano "${plan.name}" atualizado`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar plano');
    } finally {
      setSavingPlan('');
    }
  };

  const handleDeletePlan = async (plan) => {
    setSavingPlan(plan.key);
    setError('');
    try {
      const res = await api.delete(`/super-admin/plans/${plan.key}`);
      const msg = res.data?.deactivated
        ? `Plano "${plan.name}" desativado (${plan.tenants_using} cliente(s) ainda usam)`
        : `Plano "${plan.name}" eliminado`;
      flash(msg);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao eliminar plano');
    } finally {
      setSavingPlan('');
    }
  };

  const setTenantOverageMode = async (tenantId, mode) => {
    setBusyTenant(tenantId);
    setError('');
    try {
      await api.patch(`/super-admin/tenants/${tenantId}`, { overage_mode: mode });
      flash('Modo de excedente atualizado');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar modo');
    } finally {
      setBusyTenant('');
    }
  };

  const addAddon = async (tenantId) => {
    const extra = parseInt(addonForm[tenantId]?.extra || 0);
    const price = parseFloat(addonForm[tenantId]?.price || 0);
    if (!extra || extra <= 0) { setError('Indique o número de denúncias extra'); return; }
    const row = billing.find(b => b.tenant_id === tenantId);
    setConfirmAddon({ tenantId, name: row?.name || '', extra, price, total: (extra * price).toFixed(2) });
  };

  const doAddAddon = async () => {
    if (!confirmAddon) return;
    const { tenantId, extra, price } = confirmAddon;
    setConfirmAddon(null);
    setBusyTenant(tenantId);
    setError('');
    try {
      await api.post(`/super-admin/tenants/${tenantId}/addons`, {
        extra_reports: extra, unit_price: price, note: 'Add-on manual (Super Admin)',
      });
      flash('Add-on de denúncias extra registado');
      setAddonForm(prev => ({ ...prev, [tenantId]: { extra: '', price: '' } }));
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao registar add-on');
    } finally {
      setBusyTenant('');
    }
  };

  const viewInvoice = async (tenantId) => {
    setError('');
    try {
      const res = await api.get(`/super-admin/tenants/${tenantId}/invoice`);
      setInvoice({ ...res.data, tenant_id: tenantId });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao gerar fatura');
    }
  };

  const downloadInvoicePdf = async (tenantId) => {
    try {
      const res = await api.get(`/super-admin/tenants/${tenantId}/invoice.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fatura-${tenantId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erro ao descarregar PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="provisioning-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Plug className="w-6 h-6 text-indigo-700" />
          Provisionamento (CogniHub) & Planos
        </h1>
        <p className="text-slate-500 mt-1">
          Chave de API para o CogniHub criar clientes, webhooks de estado e configuração de planos
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="bg-red-900/50 border-red-500/50">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="bg-green-900/50 border-green-500/50">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <AlertDescription className="text-green-600">{success}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="api" className="space-y-6">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="api" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700" data-testid="tab-api">
            <Key className="w-4 h-4 mr-2" /> Chave de API
          </TabsTrigger>
          <TabsTrigger value="webhook" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700" data-testid="tab-webhook">
            <Webhook className="w-4 h-4 mr-2" /> Webhooks
          </TabsTrigger>
          <TabsTrigger value="plans" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700" data-testid="tab-plans">
            <Layers className="w-4 h-4 mr-2" /> Planos
          </TabsTrigger>
          <TabsTrigger value="billing" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700" data-testid="tab-billing">
            <Receipt className="w-4 h-4 mr-2" /> Faturação
          </TabsTrigger>
        </TabsList>

        <TabsContent value="api">
          <ApiKeyTab
            config={config}
            apiBase={apiBase}
            rotating={rotating}
            onRotateRequest={() => setConfirmRotate(true)}
            fixedKeyInput={fixedKeyInput}
            setFixedKeyInput={setFixedKeyInput}
            settingKey={settingKey}
            onSetKey={handleSetKey}
          />
        </TabsContent>

        <TabsContent value="webhook">
          <WebhookTab
            webhookUrl={webhookUrl}
            setWebhookUrl={setWebhookUrl}
            webhookEnabled={webhookEnabled}
            setWebhookEnabled={setWebhookEnabled}
            webhookSecret={webhookSecret}
            savingWebhook={savingWebhook}
            onSaveWebhook={handleSaveWebhook}
            webhookLogs={webhookLogs}
            onReloadLogs={load}
          />
        </TabsContent>

        <TabsContent value="plans">
          <PlansTab
            plans={plans}
            updatePlanField={updatePlanField}
            savingPlan={savingPlan}
            onSavePlan={handleSavePlan}
            onDeletePlan={handleDeletePlan}
          />
        </TabsContent>

        <TabsContent value="billing">
          <BillingTab
            billing={billing}
            addonForm={addonForm}
            setAddonForm={setAddonForm}
            busyTenant={busyTenant}
            onAddAddon={addAddon}
            onViewInvoice={viewInvoice}
            onSetTenantOverageMode={setTenantOverageMode}
          />
        </TabsContent>
      </Tabs>

      <InvoiceDialog invoice={invoice} onClose={() => setInvoice(null)} onDownloadPdf={downloadInvoicePdf} />
      <ConfirmAddonDialog confirmAddon={confirmAddon} onCancel={() => setConfirmAddon(null)} onConfirm={doAddAddon} />
      <ConfirmRotateDialog open={confirmRotate} onOpenChange={setConfirmRotate} onConfirm={handleRotate} />
      <NewKeyDialog newKey={newKey} onClose={() => setNewKey('')} />
    </div>
  );
};

export default ProvisioningPage;
